import { useEffect, useRef } from 'react';

/**
 * v5.3 — Auto-AWAY idle timer.
 *
 * Слушает user activity (mouse/keyboard/touch/visibility) и через
 * `idleMs` миллисекунд бездействия вызывает onIdle. На любое действие
 * (если был idle) вызывает onActive. visibilitychange:hidden тоже
 * triggers onIdle (taб ушёл в фон), visible → onActive.
 *
 * Никаких side effects кроме коллбэков — caller сам решает что делать
 * (мы emits status:set('AWAY') / ('ONLINE')).
 *
 * Не дёргает callbacks при размонтировании — все timers cleared.
 */
export function useIdleTimer({
  idleMs,
  onIdle,
  onActive,
}: {
  idleMs: number;
  onIdle: () => void;
  onActive: () => void;
}) {
  // Refs чтобы handlers не пересоздавались при изменении callback'ов.
  const onIdleRef = useRef(onIdle);
  const onActiveRef = useRef(onActive);
  onIdleRef.current = onIdle;
  onActiveRef.current = onActive;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    let isIdle = false;

    const setIdle = () => {
      if (!isIdle) {
        isIdle = true;
        onIdleRef.current();
      }
    };

    const setActive = () => {
      if (isIdle) {
        isIdle = false;
        onActiveRef.current();
      }
      if (timer) clearTimeout(timer);
      timer = setTimeout(setIdle, idleMs);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (timer) clearTimeout(timer);
        setIdle();
      } else {
        setActive();
      }
    };

    const events: (keyof DocumentEventMap)[] = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
      'click',
    ];
    events.forEach((e) => document.addEventListener(e, setActive, { passive: true }));
    document.addEventListener('visibilitychange', onVisibility);

    // Стартовый таймер: если юзер активен — он сразу же двинет мышью
    // и timer rearm'нется; если просто открыл и оставил — пойдёт в idle.
    timer = setTimeout(setIdle, idleMs);

    return () => {
      if (timer) clearTimeout(timer);
      events.forEach((e) => document.removeEventListener(e, setActive));
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [idleMs]);
}
